import React, { useState, useRef, useContext } from 'react';
import { Dialog, Button, FormGroup, InputGroup, Intent } from '@blueprintjs/core';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Turnstile } from '@marsidev/react-turnstile';
import { generatePDF } from 'services/pdf/api';
import { validateFirstName, validateEmail, getUserFriendlyError } from 'services/pdf/validation';
import { downloadPDF } from 'services/pdf/download';
import { mapCalculatorDataForAPI } from 'services/pdf/dataMapper';
import { ThemeContext } from 'context/ThemeProvider';
import styles from './styles.module.scss';

function PDFExportModal({ isOpen, onClose, calculatorData, language, onSuccess }) {
  const { t } = useTranslation();
  const { theme } = useContext(ThemeContext);
  const turnstileRef = useRef(null);
  const [firstName, setFirstName] = useState('');
  const [email, setEmail] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate inputs
    if (!validateFirstName(firstName)) {
      setError(t('pdf.error.firstNameRequired'));
      return;
    }

    if (!validateEmail(email)) {
      setError(t('pdf.error.emailInvalid'));
      return;
    }

    // Check if there are investments
    if (!calculatorData || calculatorData.length === 0) {
      setError(t('pdf.error.noInvestments'));
      return;
    }

    // Validate Turnstile token
    if (!turnstileToken) {
      setError(t('pdf.error.verificationFailed'));
      return;
    }

    setIsLoading(true);

    try {
      // Map calculator data to API format
      const apiData = mapCalculatorDataForAPI(calculatorData, language);

      // Generate PDF with Turnstile token
      const pdfBlob = await generatePDF(firstName.trim(), email.trim(), apiData, turnstileToken);

      // Download PDF
      downloadPDF(pdfBlob, `investment_report_${Date.now()}.pdf`);

      // Call success callback
      if (onSuccess) {
        onSuccess();
      }

      // Close modal
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to generate PDF';
      setError(getUserFriendlyError(errorMessage, t));

      // Reset Turnstile widget to get new token on error
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      setTurnstileToken('');
      setWidgetLoaded(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setFirstName('');
      setEmail('');
      setTurnstileToken('');
      setWidgetLoaded(false);
      setError(null);
      if (turnstileRef.current) {
        turnstileRef.current.reset();
      }
      onClose();
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={t('pdf.export.title')}
      className={styles.pdfExportDialog}
      canOutsideClickClose={!isLoading}
      canEscapeKeyClose={!isLoading}
    >
      <div className={styles.dialogBody}>
        <form onSubmit={handleSubmit}>
          <FormGroup label={t('pdf.export.firstName')} labelFor="firstName-input" labelInfo="*">
            <InputGroup
              id="firstName-input"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('pdf.export.firstNamePlaceholder')}
              disabled={isLoading}
              autoFocus
              large
            />
          </FormGroup>

          <FormGroup label={t('pdf.export.email')} labelFor="email-input" labelInfo="*">
            <InputGroup
              id="email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('pdf.export.emailPlaceholder')}
              disabled={isLoading}
              large
            />
          </FormGroup>

          {error && (
            <div className={styles.errorMessage} role="alert">
              {error}
            </div>
          )}

          <div className={styles.infoNote}>
            <p>{t('pdf.export.infoNote')}</p>
          </div>

          <div className={styles.turnstileWrapper}>
            <Turnstile
              ref={turnstileRef}
              siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY}
              options={{
                theme: theme || 'light',
                size: 'normal',
                refreshExpired: 'auto',
                appearance: 'interaction-only',
              }}
              onLoad={() => {
                setWidgetLoaded(true);
              }}
              onBeforeInteractive={() => {
                setWidgetLoaded(true);
              }}
              onAfterInteractive={() => {
                setWidgetLoaded(true);
              }}
              onSuccess={(token) => {
                setWidgetLoaded(true);
                setTurnstileToken(token);
                setError(null);
              }}
              onError={() => {
                setTurnstileToken('');
                setWidgetLoaded(false);
                setError(t('pdf.error.verificationFailed'));
              }}
              onExpire={() => {
                setTurnstileToken('');
              }}
              onUnsupported={() => {
                setWidgetLoaded(false);
                setError(t('pdf.error.verificationFailed'));
              }}
            />
          </div>

          {!widgetLoaded && !turnstileToken && !error && (
            <div className={styles.verificationLoading}>{t('pdf.verifying')}</div>
          )}

          <div className={styles.formActions}>
            <Button type="button" onClick={handleClose} disabled={isLoading} large>
              {t('pdf.export.cancel')}
            </Button>
            <Button
              type="submit"
              intent={Intent.PRIMARY}
              loading={isLoading}
              disabled={isLoading || !turnstileToken}
              large
            >
              {isLoading ? t('pdf.export.generating') : t('pdf.export.generate')}
            </Button>
          </div>
        </form>
      </div>
    </Dialog>
  );
}

PDFExportModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  calculatorData: PropTypes.arrayOf(PropTypes.object).isRequired,
  language: PropTypes.string,
  onSuccess: PropTypes.func,
};

PDFExportModal.defaultProps = {
  language: 'en',
  onSuccess: null,
};

export default PDFExportModal;
